import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { StripeSubscriptionInterval, StripeSubscriptionStatus } from '@/account/store/schemas'
import { selectDisplayCostPerSeatInCents, selectHasForeverPercentDiscount } from '@/account/store/selectors'
import { formatCentsToDollarsCompact } from '@/util/money'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { BillingCycle } from '@/firebase/auth/auth-jwt-schema'
import { selectMonthlyPriceForPlan } from '@/checkout/store/selectors'
import { Link } from '@/components/link/Link'
import { Collaborate, CreditCard, Settings } from '@/assets/icons'
import { kPaxtonEnterpriseEmailToHref } from '@/constants/constants-strings'
import { kMaxUsersBeforeEnterprisePricing } from '@/organizations/constants'

/**
 * Organization Pricing and Terms (settings-modal design).
 *
 * The redesigned Pricing & Terms section shown in the embedded (settings modal) create-organization
 * flow: the per-seat price, the organization benefits list, a billing caption, and the enterprise
 * contact link. The legacy account page keeps the older OrganizationSeatPricingAndTerms layout.
 *
 * The per-seat price reflects a forever-percent discount when one applies (via
 * selectDisplayCostPerSeatInCents); other discount types are shown at the standard price with a
 * "Price shown before promotions" disclaimer, matching the Subscription card. If the account's
 * billing data has not loaded, it falls back to the standard monthly plan price.
 */
const kOrganizationBenefits = [
  { icon: Collaborate, label: 'Collaborate with your team' },
  { icon: CreditCard, label: 'Centralize team billing' },
  { icon: Settings, label: 'Manage organization settings' },
]

export default function OrganizationPricingAndTerms() {
  const initialLoadComplete = useAppSelector((s: RootState) => s.accountState.initialLoadComplete)
  const isLoading = useAppSelector((s: RootState) => s.accountState.loading)

  const subscriptionStatus = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionStatus)
  const subscriptionInterval = useAppSelector((s: RootState) => s.accountState.stripeSubscriptionInterval)
  const displayCostPerSeatInCents = useAppSelector(selectDisplayCostPerSeatInCents)
  const hasDiscount = useAppSelector((s: RootState) => s.accountState.hasDiscount)
  const hasForeverPercentDiscount = useAppSelector(selectHasForeverPercentDiscount)

  // Fallback standard price, used only when the account's billing data has not loaded.
  const monthlyPlanMonthlyPrice = useAppSelector((state: RootState) => selectMonthlyPriceForPlan(state, { billingCycle: BillingCycle.MONTHLY }))

  const showDynamicPricing = initialLoadComplete && displayCostPerSeatInCents != null && subscriptionInterval != null
  const showDiscountDisclaimer = showDynamicPricing && hasDiscount && !hasForeverPercentDiscount
  const showLoader = !initialLoadComplete && isLoading

  const priceCents = showDynamicPricing ? displayCostPerSeatInCents : monthlyPlanMonthlyPrice
  const priceInterval = showDynamicPricing ? subscriptionInterval : StripeSubscriptionInterval.MONTH
  const intervalAdjective = priceInterval === StripeSubscriptionInterval.MONTH ? 'monthly' : 'annual'

  const isInTrial = subscriptionStatus === StripeSubscriptionStatus.TRIALING
  const captionText = isInTrial
    ? `Billing begins at the end of your trial. Charges will appear on your first ${intervalAdjective} invoice.`
    : 'New team members are billed per seat, prorated for the days left in your current billing cycle.'

  return (
    <div className="max-w-md space-y-4">
      <div>
        <h3 className="font-rethink-sans text-base font-semibold leading-7 text-brand-neutral-950">Pricing &amp; Terms</h3>
        {showLoader ? (
          <div className="flex min-h-6 items-center" aria-live="polite">
            <CircularProgressContinuousSized size={16} thickness={6} />
          </div>
        ) : (
          <>
            <p className="text-sm text-brand-neutral-800">
              <span className="font-bold text-brand-neutral-950">{formatCentsToDollarsCompact(priceCents)}</span> / seat / {priceInterval}
            </p>
            {showDiscountDisclaimer && <p className="text-xs text-brand-neutral-600">Price shown before promotions</p>}
          </>
        )}
      </div>

      <ul className="space-y-2">
        {kOrganizationBenefits.map(({ icon: Icon, label }) => (
          <li key={label} className="flex items-center gap-2 text-sm text-brand-neutral-800">
            <Icon className="size-4 shrink-0 text-brand-neutral-600" aria-hidden />
            {label}
          </li>
        ))}
      </ul>

      <p className="text-sm text-brand-neutral-600">{captionText}</p>

      <hr className="border-brand-neutral-200" />

      <div>
        <p className="text-sm font-semibold text-brand-neutral-950">More than {kMaxUsersBeforeEnterprisePricing} users?</p>
        <Link href={kPaxtonEnterpriseEmailToHref} external size="sm" className="font-semibold">
          Contact us for enterprise pricing
        </Link>
      </div>
    </div>
  )
}
